import React, { useState, useEffect } from 'react';
import {
  CogIcon,
  ClockIcon,
  BellIcon,
  UserGroupIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const WorkflowConfiguration = ({ data, onDataChange, user }) => {
  const [settings, setSettings] = useState({
    auto_escalation_enabled: true,
    escalation_days: 7,
    notification_reminders: {
      before_due: [3, 1],
      overdue: [1, 3, 7]
    },
    require_manager_review: true,
    allow_self_review: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadWorkflowSettings();
  }, []);

  const loadWorkflowSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
      // Load workflow settings from system_settings
      const response = await fetch('/api/probationary/settings', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        const workflowConfig = data.data.workflow_configuration || {};
        
        setSettings({
          auto_escalation_enabled: workflowConfig.auto_escalation_enabled ?? true,
          escalation_days: workflowConfig.escalation_days ?? 7,
          notification_reminders: workflowConfig.notification_reminders ?? {
            before_due: [3, 1],
            overdue: [1, 3, 7]
          },
          require_manager_review: workflowConfig.require_manager_review ?? true,
          allow_self_review: workflowConfig.allow_self_review ?? false
        });
      }
    } catch (error) {
      console.error('Error loading workflow settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('authToken');
      
      const response = await fetch('/api/probationary/settings', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          workflow_configuration: settings
        })
      });

      if (response.ok) {
        alert('Workflow configuration saved successfully!');
        onDataChange(); // Refresh parent data
      } else {
        const error = await response.json();
        alert(`Error saving settings: ${error.message}`);
      }
    } catch (error) {
      console.error('Error saving workflow settings:', error);
      alert('Error saving workflow settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleNotificationChange = (type, index, value) => {
    setSettings(prev => ({
      ...prev,
      notification_reminders: {
        ...prev.notification_reminders,
        [type]: prev.notification_reminders[type].map((item, i) => 
          i === index ? parseInt(value) : item
        )
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-lg font-medium text-gray-900">Workflow Configuration</h2>
        <p className="text-sm text-gray-500">
          Configure global settings for approval workflows
        </p>
      </div>

      {/* General Settings */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-4">
          <CogIcon className="h-6 w-6 text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900">General Settings</h3>
        </div>

        <div className="space-y-6">
          {/* Auto Escalation */}
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700">Auto Escalation</label>
              <p className="text-sm text-gray-500">
                Automatically escalate overdue approvals to the next level
              </p>
            </div>
            <button
              onClick={() => handleSettingChange('auto_escalation_enabled', !settings.auto_escalation_enabled)}
              className={`${
                settings.auto_escalation_enabled ? 'bg-blue-600' : 'bg-gray-200'
              } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
            >
              <span
                className={`${
                  settings.auto_escalation_enabled ? 'translate-x-5' : 'translate-x-0'
                } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
              />
            </button>
          </div>

          {/* Escalation Days */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Escalation Days
            </label>
            <p className="text-sm text-gray-500 mb-3">
              Number of days to wait before escalating overdue approvals
            </p>
            <input
              type="number"
              min="1"
              max="30"
              value={settings.escalation_days}
              onChange={(e) => handleSettingChange('escalation_days', parseInt(e.target.value))}
              className="w-20 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <span className="ml-2 text-sm text-gray-500">days</span>
          </div>

          {/* Manager Review Required */}
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700">Require Manager Review</label>
              <p className="text-sm text-gray-500">
                Require direct manager to conduct probationary reviews
              </p>
            </div>
            <button
              onClick={() => handleSettingChange('require_manager_review', !settings.require_manager_review)}
              className={`${
                settings.require_manager_review ? 'bg-blue-600' : 'bg-gray-200'
              } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
            >
              <span
                className={`${
                  settings.require_manager_review ? 'translate-x-5' : 'translate-x-0'
                } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
              />
            </button>
          </div>

          {/* Allow Self Review */}
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700">Allow Self Review</label>
              <p className="text-sm text-gray-500">
                Allow employees to submit self-review assessments
              </p>
            </div>
            <button
              onClick={() => handleSettingChange('allow_self_review', !settings.allow_self_review)}
              className={`${
                settings.allow_self_review ? 'bg-blue-600' : 'bg-gray-200'
              } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
            >
              <span
                className={`${
                  settings.allow_self_review ? 'translate-x-5' : 'translate-x-0'
                } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Notification Settings */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-4">
          <BellIcon className="h-6 w-6 text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900">Notification Settings</h3>
        </div>

        <div className="space-y-6">
          {/* Before Due Notifications */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notifications Before Due Date
            </label>
            <p className="text-sm text-gray-500 mb-3">
              Send reminder notifications X days before approval is due
            </p>
            <div className="flex space-x-2">
              {settings.notification_reminders.before_due.map((days, index) => (
                <div key={index} className="flex items-center space-x-1">
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={days}
                    onChange={(e) => handleNotificationChange('before_due', index, e.target.value)}
                    className="w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <span className="text-sm text-gray-500">days</span>
                </div>
              ))}
            </div>
          </div>

          {/* Overdue Notifications */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Overdue Notifications
            </label>
            <p className="text-sm text-gray-500 mb-3">
              Send reminder notifications X days after approval becomes overdue
            </p>
            <div className="flex space-x-2">
              {settings.notification_reminders.overdue.map((days, index) => (
                <div key={index} className="flex items-center space-x-1">
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={days}
                    onChange={(e) => handleNotificationChange('overdue', index, e.target.value)}
                    className="w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <span className="text-sm text-gray-500">days</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={saveSettings}
          disabled={saving}
          className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          ) : (
            <CheckCircleIcon className="h-4 w-4" />
          )}
          <span>{saving ? 'Saving...' : 'Save Configuration'}</span>
        </button>
      </div>
    </div>
  );
};

export default WorkflowConfiguration;